import {
  LogicType,
  type FormulaConditionJson,
  type FormulaJson,
  type FormulaValueJson,
  type LogicExpressionJson,
} from '@doss/common-all'

/**
 * Registers field ids from logic expression operands (column / form field / form input / nested
 * formula) so formulaString placeholders resolve in the popover.
 */
const collectFieldNamesFromLogicExpression = (
  expr: LogicExpressionJson,
  fieldNameMap: Record<string, string>,
  enqueueFormula: (f: FormulaJson) => void,
): void => {
  switch (expr.type) {
    case LogicType.BOOLEAN:
      return
    case LogicType.AND:
    case LogicType.OR:
      for (const child of expr.expressions) {
        collectFieldNamesFromLogicExpression(child, fieldNameMap, enqueueFormula)
      }
      return
    case LogicType.NOT:
      collectFieldNamesFromLogicExpression(expr.expression, fieldNameMap, enqueueFormula)
      return
    case LogicType.COMPARISON:
      collectFieldNameFromComparisonOperand(expr.left, fieldNameMap, enqueueFormula)
      collectFieldNameFromComparisonOperand(expr.right, fieldNameMap, enqueueFormula)
      return
    case LogicType.UNARY:
      collectFieldNameFromComparisonOperand(expr.value, fieldNameMap, enqueueFormula)
      return
    default:
      return
  }
}

const collectFieldNameFromComparisonOperand = (
  value: unknown,
  fieldNameMap: Record<string, string>,
  enqueueFormula: (f: FormulaJson) => void,
): void => {
  if (!value || typeof value !== 'object') {
    return
  }
  if ('operator' in value) {
    enqueueFormula(value as FormulaJson)
    return
  }
  if ('id' in value && typeof (value as { id: unknown }).id === 'string') {
    const v = value as { id: string; name?: string; label?: string }
    const displayName = 'label' in v && typeof v.label === 'string' ? v.label : v.name
    if (typeof displayName === 'string') {
      fieldNameMap[v.id] = displayName
    }
  }
}

export const buildFieldNameMap = (formula: FormulaJson): Record<string, string> => {
  const fieldNameMap: Record<string, string> = {}
  const queue: Array<FormulaJson | FormulaValueJson | FormulaConditionJson> = [formula]

  const enqueueFormula = (f: FormulaJson) => {
    queue.push(f)
  }

  while (queue.length > 0) {
    const node = queue.shift()
    if (!node) {
      continue
    }

    if ('tableColumn' in node && node.tableColumn) {
      fieldNameMap[node.tableColumn.id] = node.tableColumn.name
    }
    if ('columnOperationFilter' in node && node.columnOperationFilter) {
      collectFieldNamesFromLogicExpression(node.columnOperationFilter, fieldNameMap, enqueueFormula)
    }
    if ('condition' in node) {
      collectFieldNamesFromLogicExpression(node.condition, fieldNameMap, enqueueFormula)
    }
    if ('conditions' in node && node.conditions.length > 0) {
      queue.push(...node.conditions)
    }
    if ('formula' in node && node.formula) {
      queue.push(node.formula)
    }
    if ('formulaValues' in node) {
      queue.push(...node.formulaValues)
    }
  }

  return fieldNameMap
}
